import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { useState } from "react";
import { TFilter, TMetaData } from "types/analytics.types";
import Dropdown from "./component/dropdown";
import { useToast } from "hook/useToast";

const FilterOptions = ({
  metadata,
  filters = [],
  onChange,
}: {
  metadata: TMetaData[];
  filters?: TFilter[];
  onChange?: (filters: TFilter[]) => void;
}) => {
  const { showToast } = useToast();

  const unUsedColumns = metadata
    .filter((meta) => !filters.find((filter) => filter.name === meta.name))
    .filter((item) => item.isFilter);

  const onAddFilter = () => {
    if (unUsedColumns.length === 0) {
      showToast("더 이상 필터를 추가할 수 없습니다.", "warning");
      return;
    }
    const newFilter: TFilter = {
      name: unUsedColumns[0].name,
      columnName: unUsedColumns[0].columnName,
      contentType: unUsedColumns[0].contentType,
    };
    onChange?.([...filters, newFilter]);
  };

  return (
    <Container>
      {filters.map((filter, index) => (
        <FilterItem
          onDelete={() => {
            const new_filters = filters.filter((_, i) => i !== index);
            onChange?.(new_filters);
          }}
          unUsedColumns={unUsedColumns}
          value={filter}
          metadata={metadata}
          key={index}
          onChange={(value) => {
            const newFilters = [...filters];
            newFilters[index] = value;
            onChange?.(newFilters);
          }}
        />
      ))}
      <UI.Button primary={false} title="추가" size="small" onClick={onAddFilter} />
    </Container>
  );
};

export default FilterOptions;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
`;

const FilterItem = ({
  value,
  metadata,
  onChange,
  unUsedColumns,
  onDelete,
}: {
  value?: TFilter;
  unUsedColumns: TMetaData[];
  metadata: TMetaData[];
  onChange?: (value: TFilter) => void;
  onDelete?: () => void;
}) => {
  const [openColumn, setOpenColumn] = useState(false);
  const [openOperator, setOpenOperator] = useState(false);
  const [openValue, setOpenValue] = useState(false);
  const [openValue2, setOpenValue2] = useState(false);

  const selectedMetaData = metadata.find((item) => item.name === value?.name);

  return (
    <FilterItemContainer>
      <SelectItem id="column" onClick={() => setOpenColumn(true)}>
        <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
          {value?.name}
        </UI.Typography>
        <div className="interaction" />
        <Dropdown
          setOpen={setOpenColumn}
          open={openColumn}
          onChange={(selected: string) => {
            const selectedMetadata = metadata.find((item) => item.name === selected);
            onChange?.({
              name: selected,
              columnName: selectedMetadata?.columnName ?? "",
              contentType: selectedMetadata?.contentType ?? "",
            });
            setOpenColumn(false);
          }}
          data={unUsedColumns.filter((item) => item.isFilter).map((item) => item.name)}
        />
      </SelectItem>
      <SelectItem id="operator" onClick={() => setOpenOperator(true)}>
        <UI.Typography
          type="body01 normal"
          fontStyle="regular"
          color={value?.operator ? "var(--alias-color-primary-normal)" : "var(--alias-color-label-alternative)"}
        >
          {value?.operator ?? "연산자 선택"}
        </UI.Typography>
        <Dropdown
          setOpen={setOpenOperator}
          open={openOperator}
          onChange={(selected: string) => {
            onChange?.({ ...value, operator: selected, value: [] });
            setOpenOperator(false);
          }}
          data={selectedMetaData?.operators.map((item) => item.operator) ?? []}
        />
      </SelectItem>
      {value?.operator !== "BETWEEN" && (
        <SelectItem id="value" onClick={() => setOpenValue(true)}>
          <UI.Typography
            type="body01 normal"
            fontStyle="regular"
            color={
              value?.value && value?.value?.length !== 0
                ? "var(--alias-color-label-normal)"
                : "var(--alias-color-label-alternative)"
            }
          >
            {!value?.value || value?.value?.length === 0 ? "값 입력" : value?.value?.join(", ")}
          </UI.Typography>
          <Dropdown
            value={value?.value?.join(", ")}
            isMulti={true}
            allowSearch={true}
            onChange={(selected) => {
              const selectedList = selected.split(", ");
              onChange?.({ ...value, value: selectedList.filter((item) => item !== "") });
            }}
            setOpen={setOpenValue}
            open={openValue}
            data={selectedMetaData?.operators.find((item) => item.operator === value?.operator)?.availableValue ?? []}
          />
        </SelectItem>
      )}
      {value?.operator === "BETWEEN" && (
        <>
          <SelectItem id="value" onClick={() => setOpenValue(true)}>
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color={value?.value?.[0] ? "var(--alias-color-label-normal)" : "var(--alias-color-label-alternative)"}
            >
              {value?.value?.[0] ?? "값 입력"}
            </UI.Typography>
            <Dropdown
              value={`${value?.value?.[0]}` ?? undefined}
              isMulti={true}
              allowSearch={true}
              onChange={(selected) => {
                onChange?.({ ...value, value: [selected] });
              }}
              setOpen={setOpenValue}
              open={openValue}
              data={selectedMetaData?.operators.find((item) => item.operator === value?.operator)?.availableValue ?? []}
            />
          </SelectItem>
          <SelectItem id="value" onClick={() => setOpenValue2(true)}>
            <UI.Typography
              type="body01 normal"
              fontStyle="regular"
              color={value?.value?.[1] ? "var(--alias-color-label-normal)" : "var(--alias-color-label-alternative)"}
            >
              {value?.value?.[1] ?? "값 입력"}
            </UI.Typography>
            <Dropdown
              value={`${value?.value?.[1]}` ?? undefined}
              isMulti={true}
              allowSearch={true}
              onChange={(selected) => {
                if (value?.value && value?.value?.length > 0) {
                  onChange?.({ ...value, value: [value?.value?.[0].toString(), selected] });
                } else {
                  onChange?.({ ...value, value: [selected] });
                }
              }}
              setOpen={setOpenValue2}
              open={openValue2}
              data={selectedMetaData?.operators.find((item) => item.operator === value?.operator)?.availableValue ?? []}
            />
          </SelectItem>
        </>
      )}
      <UI.IconButton.Normal
        icon="minusCircle"
        size={24}
        color="var(--alias-color-label-assistive)"
        onClick={onDelete}
      />
    </FilterItemContainer>
  );
};

const SelectItem = styled.div`
  cursor: pointer;
  position: relative;
  display: flex;
  padding: 4px 12px;
  align-items: center;

  border-radius: 8px;
  background: var(--component-color-fill-normal);

  .interaction {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px;

    &:hover {
      background-color: var(--alias-color-label-normal);
      opacity: 0.06;
    }
    &:active {
      background-color: var(--alias-color-label-normal);
      opacity: 0.09;
    }
  }
`;

const FilterItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
